import React, { Fragment, useEffect } from "react";
import { useTable, useGlobalFilter, useAsyncDebounce, useSortBy, useFilters, useExpanded, usePagination, useRowSelect, Column, FilterProps } from "react-table";
import { Table, Row, Col, Button, Input, CardBody } from "@/Components/Common/Tailwind";
import { DefaultColumnFilter } from "./filters";
import {
  ProductsGlobalFilter,
  OrderGlobalFilter,
  ContactsGlobalFilter,
  CompaniesGlobalFilter,
  LeadsGlobalFilter,
  CryptoOrdersGlobalFilter,
  InvoiceListGlobalSearch,
  TicketsListGlobalFilter,
  NFTRankingGlobalFilter,
  TaskListGlobalFilter,
} from "../../Components/Common/GlobalSearchFilter";

// Define a default UI for filtering
function GlobalFilter({
  preGlobalFilteredRows,
  globalFilter,
  setGlobalFilter,
  isCustomerFilter,
  isOrderFilter,
  isContactsFilter,
  isCompaniesFilter,
  isCryptoOrdersFilter,
  isInvoiceListFilter,
  isTicketsListFilter,
  isNFTRankingFilter,
  isTaskListFilter,
  isProductsFilter,
  isLeadsFilter,
}: any) {
  const count = preGlobalFilteredRows.length;
  const [value, setValue] = React.useState(globalFilter);
  const onChange = useAsyncDebounce((value: any) => {
    setGlobalFilter(value || undefined);
  }, 200);

  return (
    <React.Fragment>
      <CardBody className="border border-dashed border-end-0 border-start-0">
        <form
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          <Row className="g-3">
            <Col md={3}>
              <div className={isProductsFilter || isContactsFilter || isCompaniesFilter || isNFTRankingFilter ? "search-box me-2 mb-2 inline-block" : "search-box me-2 mb-2 inline-block col-12"}>
                <input
                  onChange={(e) => {
                    setValue(e.target.value);
                    onChange(e.target.value);
                  }}
                  id="search-bar-0"
                  type="text"
                  className="form-control search /"
                  placeholder={`${count} Search...`}
                  value={value || ""}
                />
                <i className="bx bx-search-alt search-icon"></i>
              </div>
            </Col>
            {isProductsFilter && <ProductsGlobalFilter />}
            {/* {isCustomerFilter && (
              <CustomersGlobalFilter />
            )} */}
            {isOrderFilter && <OrderGlobalFilter />}
            {isContactsFilter && <ContactsGlobalFilter />}
            {isCompaniesFilter && <CompaniesGlobalFilter />}
            {isLeadsFilter && <LeadsGlobalFilter />}
            {isCryptoOrdersFilter && <CryptoOrdersGlobalFilter />}
            {isInvoiceListFilter && <InvoiceListGlobalSearch />}
            {isTicketsListFilter && <TicketsListGlobalFilter />}
            {isNFTRankingFilter && <NFTRankingGlobalFilter />}
            {isTaskListFilter && <TaskListGlobalFilter />}
          </Row>
        </form>
      </CardBody>
    </React.Fragment>
  );
}

interface TableContainerProps {
  defaultPage?: number;
  onChangePage?: (page: number) => void;
  columns: any[];
  data: any[];
  isGlobalSearch?: boolean;
  isGlobalFilter?: boolean;
  isProductsFilter?: boolean;
  isCustomerFilter?: boolean;
  isOrderFilter?: boolean;
  isContactsFilter?: boolean;
  isCompaniesFilter?: boolean;
  isLeadsFilter?: boolean;
  isCryptoOrdersFilter?: boolean;
  isInvoiceListFilter?: boolean;
  isTicketsListFilter?: boolean;
  isNFTRankingFilter?: boolean;
  isTaskListFilter?: boolean;
  isAddOptions?: boolean;
  isAddUserList?: boolean;
  handleOrderClicks?: () => void;
  handleUserClick?: () => void;
  handleCustomerClick?: () => void;
  isAddCustList?: boolean;
  customPageSize?: number;
  tableClass?: string;
  theadClass?: string;
  trClass?: string;
  thClass?: string;
  divClass?: string;
}

interface TableColumnHeader {
  id: string;
  render: (type: string) => React.ReactNode;
  getSortByToggleProps: () => React.HTMLAttributes<HTMLTableCellElement>;
}

interface TableHeaderGroup {
  id: string;
  headers: TableColumnHeader[];
  getHeaderGroupProps: () => React.HTMLAttributes<HTMLTableRowElement>;
}

interface TableCell {
  id: string;
  column: {
    width?: number | string;
  };
  getCellProps: (props?: { style?: React.CSSProperties }) => React.HTMLAttributes<HTMLTableCellElement>;
  render: (type: string) => React.ReactNode;
}

interface TableRow {
  getRowProps: () => React.HTMLAttributes<HTMLTableRowElement> & { key?: React.Key };
  cells: TableCell[];
}

const TableContainer = React.forwardRef<HTMLTableElement, TableContainerProps>(function TableContainer(
  {
    defaultPage,
    onChangePage,
    columns,
    data,
    isGlobalSearch,
    isGlobalFilter,
    isProductsFilter,
    isCustomerFilter,
    isOrderFilter,
    isContactsFilter,
    isCompaniesFilter,
    isLeadsFilter,
    isCryptoOrdersFilter,
    isInvoiceListFilter,
    isTicketsListFilter,
    isNFTRankingFilter,
    isTaskListFilter,
    isAddOptions,
    isAddUserList,
    handleOrderClicks,
    handleUserClick,
    handleCustomerClick,
    isAddCustList,
    customPageSize,
    tableClass,
    theadClass,
    trClass,
    thClass,
    divClass,
  },
  ref
) {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state,
    preGlobalFilteredRows,
    setGlobalFilter,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data,
      defaultColumn: { Filter: DefaultColumnFilter },
      initialState: {
        pageIndex: 0,
        pageSize: customPageSize,
        selectedRowIds: {},
        sortBy: [
          // {
          //   id: 'id',
          //   desc: true,
          // },
          // {
          //   id: 'is_notice',
          //   desc: true,
          // },
        ],
      } as any,
    },
    useGlobalFilter,
    useFilters,
    useSortBy,
    useExpanded,
    usePagination,
    useRowSelect
  );

  const generateSortingIndicator = (column: any) => {
    return column.isSorted ? (column.isSortedDesc ? " " : "") : "";
  };

  const onChangeInSelect = (event: any) => {
    setPageSize(Number(event.target.value));
  };
  const onChangeInInput = (event: any) => {
    const page = event.target.value ? Number(event.target.value) - 1 : 0;
    gotoPage(page);
  };

  useEffect(() => {
    if (defaultPage) gotoPage(defaultPage);
    // console.log("defaultPage", defaultPage);
  }, [defaultPage]);

  useEffect(() => {
    if (onChangePage) onChangePage(pageIndex);
    // console.log("pageIndex", pageIndex);
  }, [pageIndex]);

  return (
    <Fragment>
      <Row className="mb-3">
        {isGlobalSearch && (
          <Col md={1}>
            <select className="form-select" value={pageSize} onChange={onChangeInSelect}>
              {[10, 20, 30, 40, 50].map((pageSize) => (
                <option key={pageSize} value={pageSize}>
                  Show {pageSize}
                </option>
              ))}
            </select>
          </Col>
        )}
        {isGlobalFilter && (
          <GlobalFilter
            preGlobalFilteredRows={preGlobalFilteredRows}
            globalFilter={state.globalFilter}
            setGlobalFilter={setGlobalFilter}
            isProductsFilter={isProductsFilter}
            isCustomerFilter={isCustomerFilter}
            isOrderFilter={isOrderFilter}
            isContactsFilter={isContactsFilter}
            isCompaniesFilter={isCompaniesFilter}
            isLeadsFilter={isLeadsFilter}
            isCryptoOrdersFilter={isCryptoOrdersFilter}
            isInvoiceListFilter={isInvoiceListFilter}
            isTicketsListFilter={isTicketsListFilter}
            isNFTRankingFilter={isNFTRankingFilter}
            isTaskListFilter={isTaskListFilter}
          />
        )}
        {isAddOptions && (
          <Col sm="7">
            <div className="text-sm-end">
              <Button type="button" variant="primary" className="mb-2 me-2" onClick={handleOrderClicks}>
                <i className="mdi mdi-plus me-1" />
                Add New Order
              </Button>
            </div>
          </Col>
        )}
        {isAddUserList && (
          <Col sm="7">
            <div className="text-sm-end">
              <Button type="button" variant="primary" className="mb-2 me-2" onClick={handleUserClick}>
                <i className="mdi mdi-plus-circle-outline me-1" />
                Create New User
              </Button>
            </div>
          </Col>
        )}
        {isAddCustList && (
          <Col sm="7">
            <div className="text-sm-end">
              <Button type="button" variant="primary" className="mb-2 me-2" onClick={handleCustomerClick}>
                <i className="mdi mdi-plus me-1" />
                New Customers
              </Button>
            </div>
          </Col>
        )}
      </Row>

      <div className={divClass}>
        <Table hover {...getTableProps()} className={tableClass} innerRef={ref}>
          <thead className={theadClass}>
            {headerGroups.map((headerGroup: TableHeaderGroup) => (
              <tr className={trClass} key={headerGroup.id} {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column: TableColumnHeader) => (
                  <th key={column.id} className={thClass} {...column.getSortByToggleProps()}>
                    {column.render("Header")}
                    {generateSortingIndicator(column)}
                    {/* <Filter column={column} /> */}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody {...getTableBodyProps()}>
            {page.map((row: TableRow) => {
              prepareRow(row);
              return (
                <Fragment key={row.getRowProps().key}>
                  <tr>
                    {row.cells.map((cell: TableCell) => {
                      return (
                        <td
                          {...cell.getCellProps({
                            style: {
                              width: cell.column.width,
                              paddingLeft: "6px",
                              // paddingBottom: "6px",
                              paddingRight: 0,
                            },
                          })}
                          key={cell.id}
                        >
                          <div style={{ width: cell.column.width + "px" }}>{cell.render("Cell")}</div>
                        </td>
                      );
                    })}
                  </tr>
                </Fragment>
              );
            })}
          </tbody>
        </Table>
      </div>

      {pageOptions && pageOptions.length > 1 ? (
        <Row className="flex justify-content-md-end justify-center items-center p-2">
          <div className="pb-4" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            {/* <Col className="col-md-auto"> */}
            <div className="flex gap-1">
              <Button
                onClick={previousPage}
                disabled={!canPreviousPage}
                style={{ color: "#F6475F", backgroundColor: "transparent", border: "none", fontSize: "18px", fontFamily: "Gmarket-M", boxShadow: "none" }}
              >
                {"<"}
              </Button>
            </div>
            {/* </Col> */}
            {/* <Col className="col-md-auto">
          <Input
            type="number"
            min={1}
            style={{ width: 70 }}
            max={pageOptions.length}
            // defaultValue={pageIndex + 1}
            value={pageIndex + 1}
            onChange={onChangeInInput}
          />
        </Col> */}
            {/* <Col className="col-md-auto d-block d-md-block"> */}
            <span style={{ fontFamily: "Gmarket-M", width: "90px", textAlign: "center" }}>
              {Number(pageIndex) + 1} / {pageOptions.length}
            </span>
            {/* </Col> */}

            {/* <Col className="col-md-auto"> */}
            <div className="flex gap-1">
              <Button
                onClick={nextPage}
                disabled={!canNextPage}
                style={{ color: "#F6475F", backgroundColor: "transparent", border: "none", fontSize: "18px", fontFamily: "Gmarket-M", boxShadow: "none" }}
              >
                {">"}
              </Button>
            </div>
            {/* </Col> */}
          </div>
        </Row>
      ) : null}
    </Fragment>
  );
});

export default TableContainer;
